import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, map, tap, throwError } from 'rxjs';
import { PaginatedResponse, PaginationMeta } from '@shared/models';
import { SseNotificationService } from '@shared/services/sse-notification.service';
import {
  NotificationDto,
  NotificationEntity,
  NotificationFilterDto,
} from '../models';
import { NotificationApiService } from './notification-api.service';

interface UnreadCountPayload {
  count: number;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationStore {
  private readonly api = inject(NotificationApiService);
  private readonly sse = inject(SseNotificationService);

  private readonly _notifications = signal<NotificationEntity[]>([]);
  private readonly _unreadCount = signal<number>(0);
  private readonly _loading = signal<boolean>(false);
  private readonly _saving = signal<boolean>(false);
  private readonly _meta = signal<PaginationMeta | null>(null);
  private readonly _error = signal<string | null>(null);
  private initialized = false;

  readonly notifications = this._notifications.asReadonly();
  readonly unreadCount = this._unreadCount.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly saving = this._saving.asReadonly();
  readonly meta = this._meta.asReadonly();
  readonly error = this._error.asReadonly();

  readonly hasUnread = computed(() => this._unreadCount() > 0);
  readonly isEmpty = computed(
    () => this._notifications().length === 0 && !this._loading(),
  );

  constructor() {
    this.sse.on('notification.new').subscribe(event => {
      const data = event.data as NotificationDto;
      const entity = new NotificationEntity(data);
      this._notifications.update(list => [entity, ...list]);
    });

    this.sse.on('notification.unread_count_changed').subscribe(event => {
      const data = event.data as UnreadCountPayload;
      if (typeof data?.count === 'number') {
        this._unreadCount.set(data.count);
      }
    });
  }

  initOnce(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.loadList({ page: 1, limit: 10 }).subscribe({ error: () => undefined });
  }

  loadList(filter: NotificationFilterDto = {}): Observable<PaginatedResponse<NotificationEntity>> {
    this._loading.set(true);
    this._error.set(null);
    return this.api.list(filter).pipe(
      map(response => ({
        ...response,
        data: response.data.map(dto => new NotificationEntity(dto)),
      })),
      tap(response => {
        this._notifications.set(response.data);
        this._meta.set(response.meta);
        this._loading.set(false);
      }),
      catchError(error => {
        this._loading.set(false);
        this._error.set(error?.error?.errorCode ?? 'NOTIFICATIONS.LOAD_ERROR');
        return throwError(() => error);
      }),
    );
  }

  markAsRead(id: number): Observable<NotificationEntity> {
    this._saving.set(true);
    return this.api.markAsRead(id).pipe(
      map(dto => new NotificationEntity(dto)),
      tap(updated => {
        this._notifications.update(list =>
          list.map(n => (n.id === id ? updated : n)),
        );
        this._saving.set(false);
      }),
      catchError(error => {
        this._saving.set(false);
        return throwError(() => error);
      }),
    );
  }

  markAllAsRead(): Observable<void> {
    this._saving.set(true);
    return this.api.markAllAsRead().pipe(
      tap(() => {
        const now = new Date().toISOString();
        this._notifications.update(list =>
          list.map(n => {
            if (n.readAt !== null) return n;
            return new NotificationEntity({
              id: n.id,
              userId: n.userId,
              type: n.type,
              payload: n.payload,
              link: n.link,
              readAt: now,
              createdAt: n.createdAt,
              updatedAt: now,
            });
          }),
        );
        this._saving.set(false);
      }),
      catchError(error => {
        this._saving.set(false);
        return throwError(() => error);
      }),
    );
  }
}
